import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Zap, Users, Star, CheckCircle2, Bell, Loader2, UserPlus } from 'lucide-react';
import { notificationsApi, groupsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';

const iconMap = {
  Zap: Zap,
  Users: Users,
  Star: Star,
  CheckCircle2: CheckCircle2,
  Bell: Bell,
  UserPlus: UserPlus,
};

// Helper to categorize notifications by month (sorted recent first)
const categorizeByMonth = (notifications) => {
  // Sort notifications by date, most recent first
  const sorted = [...notifications].sort((a, b) => {
    const dateA = new Date(a.created_at || a.createdAt || a.date);
    const dateB = new Date(b.created_at || b.createdAt || b.date);
    return dateB - dateA;
  });

  // Group by month
  const monthGroups = {};

  sorted.forEach((notif) => {
    const notifDate = new Date(notif.created_at || notif.createdAt || notif.date);
    const monthKey = `${notifDate.getFullYear()}-${String(notifDate.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = notifDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    if (!monthGroups[monthKey]) {
      monthGroups[monthKey] = { label: monthLabel, notifications: [] };
    }
    monthGroups[monthKey].notifications.push(notif);
  });

  // Convert to sorted array (most recent month first)
  return Object.entries(monthGroups)
    .sort(([keyA], [keyB]) => keyB.localeCompare(keyA))
    .map(([, value]) => value);
};

// Helper to format date as "Jan 2, 2026"
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function NotificationsPanel({ onClose }) {
  const navigate = useNavigate();
  const { isDesktop } = useResponsive();
  const { isGuestMode, setShowGuestPrompt } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [joining, setJoining] = useState(false);
  const [limit, setLimit] = useState(30); // Pagination limit - show 30 at a time

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await notificationsApi.getAll();
        setNotifications(data);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    // Optimistically update UI first (set all possible field names including hasRead)
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId || n.uuid === notificationId
        ? { ...n, hasRead: true, is_read: true, isRead: true, read: true }
        : n))
    );

    // Then make API call
    try {
      await notificationsApi.markRead(notificationId);
      console.log('Notification marked as read:', notificationId);
    } catch (err) {
      console.error('Failed to mark as read:', err);
      // Revert on error
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId || n.uuid === notificationId
          ? { ...n, hasRead: false, is_read: false, isRead: false, read: false }
          : n))
      );
    }
  };

  // Handle notification click - show invite drawer for GROUP notifications
  const handleNotificationClick = (notif) => {
    const notifId = notif.uuid || notif.id;

    // Mark as read first
    markAsRead(notifId);

    // If it's a GROUP invitation, show the invite drawer
    if (notif.category === 'GROUP' && notif.type === 'GROUP_INVITATION') {
      setSelectedInvite(notif);
    }
  };

  // Handle join group button click
  const handleJoinGroup = async () => {
    if (!selectedInvite) return;

    // Guest users need to sign up to join groups
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    // Extract groupId from notification (check various field names)
    const groupId = selectedInvite.groupId || selectedInvite.group_id || selectedInvite.metadata?.groupId;

    if (!groupId) {
      console.error('No groupId found in notification:', selectedInvite);
      // Still navigate to groups page even if we can't find the groupId
      setSelectedInvite(null);
      onClose();
      navigate('/groups');
      return;
    }

    setJoining(true);
    try {
      // Accept the group invitation
      await groupsApi.acceptInvite(groupId);
      console.log('Successfully joined group:', groupId);

      // Close the invite drawer and notifications panel
      setSelectedInvite(null);
      onClose();

      // Navigate to groups page to see the group
      navigate('/groups');
    } catch (err) {
      console.error('Failed to join group:', err);
      // Still close and navigate even on error (user can try again from groups page)
      setSelectedInvite(null);
      onClose();
      navigate('/groups');
    } finally {
      setJoining(false);
    }
  };

  // Paginate notifications - sort by date (recent first) and show up to limit
  const sortedNotifications = [...notifications].sort((a, b) => {
    const dateA = new Date(a.created_at || a.createdAt || a.date);
    const dateB = new Date(b.created_at || b.createdAt || b.date);
    return dateB - dateA;
  });
  const paginatedNotifications = sortedNotifications.slice(0, limit);
  const hasMore = sortedNotifications.length > limit;
  const monthGroups = categorizeByMonth(paginatedNotifications);

  const handleLoadMore = () => {
    setLimit((prev) => prev + 30);
  };

  const renderNotification = (notif) => {
    // Handle both legacy format and Boldscale API format (check all possible field names including hasRead)
    const isRead = notif.hasRead || notif.is_read || notif.isRead || notif.read || false;
    const notifId = notif.uuid || notif.id;
    const title = notif.title || notif.notification_title || 'Notification';
    const body = notif.body || notif.message || notif.notification_body || '';
    const time = formatDate(notif.created_at || notif.createdAt || notif.date);

    // Choose icon based on notification type
    let Icon = Bell;
    if (notif.category === 'GROUP' && notif.type === 'GROUP_INVITATION') {
      Icon = UserPlus;
    } else if (notif.iconType && iconMap[notif.iconType]) {
      Icon = iconMap[notif.iconType];
    }

    return (
      <button
        key={notifId}
        onClick={() => handleNotificationClick(notif)}
        className={`w-full flex items-start gap-3 p-4 transition-colors text-left ${
          isRead ? 'bg-white hover:bg-gray-50' : 'bg-indigo-50 hover:bg-indigo-100'
        }`}
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            isRead ? 'bg-gray-100 text-gray-500' : 'bg-indigo-100 text-indigo-600'
          }`}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm ${isRead ? 'text-gray-600' : 'text-indigo-600 font-semibold'}`}>
            {title}
          </p>
          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{body}</p>
          <p className="text-xs text-gray-400 mt-1">{time}</p>
        </div>
        {!isRead && (
          <div className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0 mt-2" />
        )}
      </button>
    );
  };

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={onClose} />

      {/* Panel - Full height on desktop, 80% on mobile */}
      <div
        className={`fixed z-[95] bg-white ${
          isDesktop
            ? 'top-0 right-0 bottom-0 w-96 h-full border-l border-gray-200'
            : 'left-0 right-0 bottom-0 h-[80%] max-h-[80vh] rounded-t-3xl'
        }`}
        style={{ animation: isDesktop ? 'slideInRight 0.3s ease-out' : 'slideUp 0.3s ease-out' }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-lg font-bold">Notifications</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="text-indigo-600 animate-spin" />
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell size={24} className="text-red-400" />
                </div>
                <p className="text-gray-500">Failed to load notifications</p>
                <p className="text-sm text-gray-400 mt-1">{error}</p>
              </div>
            )}

            {/* Notifications List - Grouped by Month */}
            {!loading && !error && (
              <>
                {monthGroups.map((monthGroup) => (
                  <div key={monthGroup.label}>
                    <h3 className="text-xs font-bold text-gray-500 uppercase py-3">
                      {monthGroup.label}
                    </h3>
                    <div className="-mx-4">
                      {monthGroup.notifications.map((notif, index) => (
                        <div key={notif.uuid || notif.id}>
                          {renderNotification(notif)}
                          {index < monthGroup.notifications.length - 1 && (
                            <div className="border-b border-gray-100" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Load More Button */}
                {hasMore && paginatedNotifications.length > 0 && (
                  <div className="pt-2 pb-4">
                    <button
                      onClick={handleLoadMore}
                      className="w-full py-3 text-indigo-600 font-medium text-sm hover:bg-indigo-50 rounded-xl transition-colors"
                    >
                      Load More
                    </button>
                  </div>
                )}

                {paginatedNotifications.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bell size={24} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500">No notifications yet</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Group Invite Drawer */}
      {selectedInvite && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[100]"
            onClick={() => setSelectedInvite(null)}
          />
          <div
            className={`fixed z-[101] bg-white ${
              isDesktop
                ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 rounded-2xl'
                : 'left-4 right-4 bottom-4 rounded-2xl'
            }`}
            style={{ animation: 'slideUp 0.3s ease-out' }}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Group Invitation</h3>
                <button
                  onClick={() => setSelectedInvite(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Invite Content */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                  <UserPlus size={32} className="text-indigo-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{selectedInvite.title}</h4>
                <p className="text-gray-600">{selectedInvite.message || selectedInvite.body}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {formatDate(selectedInvite.created_at || selectedInvite.createdAt)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedInvite(null)}
                  disabled={joining}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Maybe Later
                </button>
                <button
                  onClick={handleJoinGroup}
                  disabled={joining}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {joining ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Join Group'
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
